/**
 * "View as technician" — a read-only session of a technician's portal,
 * confined to ONE browser tab.
 *
 * The app keeps the login token in localStorage, which every tab shares, and
 * many components read it directly. Swapping it would silently turn the
 * admin's other tabs into the technician. Instead, the admin tab opens a new
 * tab at /technician-portal#viewAs=<token>; at startup that tab stores the
 * session in sessionStorage (per tab) and routes this tab's reads/writes of
 * the login keys there. Other tabs keep the admin login untouched.
 *
 * The backend enforces read-only access (every write is refused), so this
 * module only decides which token this tab sends.
 */
const SESSION_KEY = 'primeshine.viewAs';
const OVERLAY_PREFIX = 'primeshine.viewAs.';
const LOGIN_KEYS = new Set(['token', 'user']);

const readSession = () => {
  try {
    const session = JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || 'null');
    if (!session || !session.token) return null;
    if (session.expiresAt && Date.now() > session.expiresAt) {
      window.sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
};

/** The active view-as session for this tab ({ token, name, expiresAt }), or null. */
export const getViewAsSession = () => readSession();

/** Must run before React renders (index.js). */
export const installViewAsSession = () => {
  // 1. Hand-off from the admin tab: #viewAs=<token>&name=<technician>&ttl=<seconds>
  const hash = new URLSearchParams(window.location.hash.slice(1));
  if (hash.get('viewAs')) {
    const ttl = Number(hash.get('ttl')) || 3600;
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({
      token: hash.get('viewAs'),
      name: hash.get('name') || 'technician',
      expiresAt: Date.now() + ttl * 1000,
    }));
    // Remove the token from the address bar and history
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  if (!readSession()) return;

  // 2. In this tab only, the login keys live in sessionStorage, not the shared localStorage
  const proto = window.Storage.prototype;
  const original = { getItem: proto.getItem, setItem: proto.setItem, removeItem: proto.removeItem };
  const isLoginKey = (storage, key) => storage === window.localStorage && LOGIN_KEYS.has(key) && readSession();

  proto.getItem = function getItem(key) {
    if (isLoginKey(this, key)) {
      return key === 'token'
        ? readSession().token
        : original.getItem.call(window.sessionStorage, OVERLAY_PREFIX + key);
    }
    return original.getItem.call(this, key);
  };
  proto.setItem = function setItem(key, value) {
    if (isLoginKey(this, key)) {
      if (key !== 'token') original.setItem.call(window.sessionStorage, OVERLAY_PREFIX + key, value);
      return undefined;
    }
    return original.setItem.call(this, key, value);
  };
  proto.removeItem = function removeItem(key) {
    if (isLoginKey(this, key)) {
      // Logging out here ends the view-as session, never the admin's login
      if (key === 'token') original.removeItem.call(window.sessionStorage, SESSION_KEY);
      original.removeItem.call(window.sessionStorage, OVERLAY_PREFIX + key);
      return undefined;
    }
    return original.removeItem.call(this, key);
  };
};

/** End the session; the tab closes if the admin tab opened it. */
export const exitViewAsSession = () => {
  window.sessionStorage.removeItem(SESSION_KEY);
  LOGIN_KEYS.forEach((key) => window.sessionStorage.removeItem(OVERLAY_PREFIX + key));
  window.close();
  // window.close() is ignored when the tab wasn't script-opened: go back to the admin side
  window.location.assign('/maid-profile');
};

/**
 * Admin side: open a technician's portal read-only in a new tab.
 * Call from a click handler (the tab is opened synchronously to avoid popup blockers).
 */
export const openViewAsTab = async ({ apiBase, technicianUserId, authToken }) => {
  const tab = window.open('about:blank', '_blank');
  try {
    const res = await fetch(`${apiBase}/api/technicians/${technicianUserId}/view-as`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not open the technician view');
    const params = new URLSearchParams({ viewAs: data.token, name: data.technician.name, ttl: String(data.expiresIn) });
    if (tab) {
      tab.opener = null;
      tab.location.href = `/technician-portal#${params.toString()}`;
    }
    return data;
  } catch (error) {
    if (tab) tab.close();
    throw error;
  }
};
